import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useActiveCategories } from "../lib/meta";
import { formatDate, formatINR, formatPoints } from "../lib/format";
import type { Card, EmailAlertRow, GmailStatus, GmailSyncOutcome, Person, SkippedAlert } from "../lib/types";
import { Badge, EmptyState, ErrorBanner, PageHeader, Panel, Spinner, StatTile } from "../components/ui";

function describeOutcome(outcome: GmailSyncOutcome): string {
  switch (outcome.status) {
    case "done":
      if (outcome.read === 0) return "Checked — no new alerts.";
      return [
        `Read ${outcome.read} new email${outcome.read === 1 ? "" : "s"}`,
        outcome.added && `${outcome.added} added`,
        outcome.review && `${outcome.review} to look at`,
        outcome.duplicates && `${outcome.duplicates} already logged`,
        outcome.skipped && `${outcome.skipped} not purchases`,
      ]
        .filter(Boolean)
        .join(" · ");
    case "recent":
      return "Checked a few minutes ago.";
    case "not-connected":
      return "No inbox is connected.";
    default:
      return outcome.error;
  }
}

export default function EmailPage() {
  const [params, setParams] = useSearchParams();
  const categories = useActiveCategories();
  const [status, setStatus] = useState<GmailStatus | null>(null);
  const [alerts, setAlerts] = useState<EmailAlertRow[] | null>(null);
  const [skipped, setSkipped] = useState<SkippedAlert[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [justConnected, setJustConnected] = useState(false);

  // Google sends the browser back here with the result in the address.
  useEffect(() => {
    const result = params.get("gmail");
    if (!result) return;
    if (result === "connected") {
      setNotice("Gmail is connected. Checking for alerts now…");
      setJustConnected(true);
    } else setError(params.get("message") ?? "Could not connect Gmail.");
    setParams({}, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    try {
      const [s, a, c, p] = await Promise.all([
        api.get<GmailStatus>("/gmail"),
        api.get<{ alerts: EmailAlertRow[]; skipped: SkippedAlert[] }>("/gmail/alerts"),
        api.get<{ cards: Card[] }>("/cards"),
        api.get<{ people: Person[] }>("/people"),
      ]);
      setStatus(s);
      setAlerts(a.alerts);
      setSkipped(a.skipped);
      setCards(c.cards);
      setPeople(p.people);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load your email alerts");
      setAlerts([]);
    }
  }, []);

  const check = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await api.post<GmailSyncOutcome>("/gmail/sync", { auto: false });
      if (outcome.status === "failed" || outcome.status === "needs-reconnect") setError(outcome.error);
      else setNotice(describeOutcome(outcome));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The check failed");
    } finally {
      setBusy(false);
    }
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Straight after connecting, read the inbox rather than make them press a button.
  useEffect(() => {
    if (!justConnected) return;
    setJustConnected(false);
    void check();
  }, [justConnected, check]);

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>("/gmail/connect");
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start connecting Gmail");
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!confirm("Stop reading this inbox? Purchases already added stay in your ledger; the list of emails read is cleared.")) return;
    setBusy(true);
    try {
      await api.del("/gmail");
      setNotice("Gmail is disconnected, and the app's access has been withdrawn with Google.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  }

  async function act(alert: EmailAlertRow, action: "dismiss" | "remove") {
    setError(null);
    try {
      await api.post(`/gmail/alerts/${alert.id}/${action}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work");
    }
  }

  const conn = status?.connection ?? null;
  const toLook = (alerts ?? []).filter((a) => a.status === "REVIEW" || a.status === "DUPLICATE");
  const added = (alerts ?? []).filter((a) => a.status === "ADDED");
  const pending = added.filter((a) => a.transaction?.verification === "PENDING");

  return (
    <div>
      <PageHeader
        title="Email alerts"
        subtitle="Your bank emails you seconds after the card is used. Those alerts land here as pending purchases, and the statement confirms them later."
        actions={
          conn && (
            <button className="btn-primary" disabled={busy} onClick={() => void check()}>
              {busy ? "Checking…" : "Check now"}
            </button>
          )
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <p className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">{notice}</p>
      )}

      {status === null ? (
        <Spinner />
      ) : !status.configured ? (
        <SetupPanel redirectUri={status.redirectUri} />
      ) : !conn ? (
        <Panel title="Connect Gmail" className="mb-6">
          <p className="mb-3 text-sm text-ink-600">
            The app asks Google for <strong>read-only</strong> access. It can't send, delete or change mail. It
            searches only for alerts from HDFC, Axis, HSBC and American Express, and reads the last 30 days the
            first time.
          </p>
          <p className="mb-4 text-sm text-ink-600">
            Google will warn that the app isn't verified. That is expected for an app you run yourself: choose
            <em> Advanced → Go to the app</em>.
          </p>
          <button className="btn-primary" disabled={busy} onClick={() => void connect()}>Connect Gmail</button>
        </Panel>
      ) : (
        <>
          <Panel
            title={conn.email}
            subtitle={
              conn.lastSyncedAt
                ? `Last checked ${formatDate(conn.lastSyncedAt)} at ${new Date(conn.lastSyncedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : "Not checked yet"
            }
            className="mb-6"
            actions={
              <div className="flex gap-2">
                {conn.needsReconnect && (
                  <button className="btn-primary" disabled={busy} onClick={() => void connect()}>Connect again</button>
                )}
                <button className="btn-secondary" disabled={busy} onClick={() => void disconnect()}>Disconnect</button>
              </div>
            }
          >
            {conn.lastError ? (
              <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{conn.lastError}</p>
            ) : (
              <p className="text-sm text-ink-600">
                New alerts are read whenever you open the app. Only HDFC, Axis, HSBC and Amex alerts are
                looked at.
              </p>
            )}
          </Panel>

          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <StatTile label="Added, waiting on a statement" value={String(pending.length)} sub="Points already counted" />
            <StatTile label="Needs a look" value={String(toLook.length)} tone={toLook.length ? "warning" : "default"} />
            <StatTile
              label="Spend not yet confirmed"
              value={formatINR(pending.reduce((sum, a) => sum + (a.amount ?? 0), 0))}
            />
          </div>

          {toLook.length > 0 && (
            <Panel title="Needs a look" subtitle="Nothing is added from these until you say so." className="mb-6">
              <div className="space-y-4">
                {toLook.map((alert) => (
                  <ReviewItem
                    key={alert.id}
                    alert={alert}
                    cards={cards}
                    people={people}
                    categories={categories}
                    onDone={load}
                    onDismiss={() => void act(alert, "dismiss")}
                    onError={setError}
                  />
                ))}
              </div>
            </Panel>
          )}

          <Panel title="Added from email" subtitle="Pending until the statement shows the same purchase." className="mb-6">
            {alerts === null ? (
              <Spinner />
            ) : added.length === 0 ? (
              <EmptyState title="Nothing added yet" hint="Use a card and its alert shows up here the next time the app checks." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Merchant</th>
                      <th className="py-2 pr-3">Card</th>
                      <th className="py-2 pr-3 text-right">Amount</th>
                      <th className="py-2 pr-3 text-right">Points</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {added.map((alert) => (
                      <tr key={alert.id} className="border-b border-ink-100">
                        <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-ink-600">{alert.date ?? "—"}</td>
                        <td className="min-w-0 py-2 pr-3">
                          <span className="block truncate font-medium text-ink-900">{alert.merchant ?? "—"}</span>
                        </td>
                        <td className="py-2 pr-3">
                          {alert.transaction ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: alert.transaction.cardColor }} aria-hidden />
                              <span className="text-ink-700">{alert.transaction.cardName}</span>
                            </span>
                          ) : "—"}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{alert.amount != null ? formatINR(alert.amount) : "—"}</td>
                        <td className="py-2 pr-3 text-right tabular-nums text-ink-600">
                          {formatPoints(alert.transaction?.pointsAwarded ?? 0)}
                        </td>
                        <td className="py-2 pr-3">
                          {alert.transaction?.verification === "CONFIRMED" ? (
                            <Badge tone="good">on the statement</Badge>
                          ) : alert.transaction ? (
                            <Badge tone="warning">pending</Badge>
                          ) : (
                            <Badge tone="neutral">deleted</Badge>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          {alert.transaction?.verification === "PENDING" && (
                            <button className="btn-ghost text-xs text-rose-700" onClick={() => void act(alert, "remove")}>
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {skipped.length > 0 && (
            <details className="card-surface p-4">
              <summary className="cursor-pointer text-sm font-medium text-ink-700">
                Emails read that were not purchases ({skipped.length}{skipped.length === 50 ? "+" : ""})
              </summary>
              <ul className="mt-3 space-y-1 text-sm">
                {skipped.map((s) => (
                  <li key={s.id} className="flex flex-wrap gap-x-3 text-ink-600">
                    <span className="tabular-nums text-ink-400">{formatDate(s.receivedAt)}</span>
                    <span className="min-w-0 truncate text-ink-800">{s.subject || "(no subject)"}</span>
                    <span className="text-ink-500">— {s.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function SetupPanel({ redirectUri }: { redirectUri: string }) {
  return (
    <Panel title="Set up Gmail access" subtitle="A one-off job on Google's side, about ten minutes." className="mb-6">
      <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-700">
        <li>
          In the <a className="text-accent-700 underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">Google Cloud console</a>,
          create a project and enable the <strong>Gmail API</strong>.
        </li>
        <li>
          Under <strong>OAuth consent screen</strong>, choose <em>External</em>, add the scope
          <code className="mx-1 rounded bg-ink-100 px-1">gmail.readonly</code>, then set the publishing status to
          <strong> In production</strong>. Leaving it in <em>Testing</em> makes Google cut the connection every 7 days.
        </li>
        <li>
          Under <strong>Credentials</strong>, create an <em>OAuth client ID</em> of type <em>Web application</em>, with this
          as its authorised redirect URI:
          <code className="mt-1 block break-all rounded bg-ink-100 px-2 py-1">{redirectUri}</code>
        </li>
        <li>
          Put the client ID and secret in the server's environment as <code>GOOGLE_CLIENT_ID</code> and
          <code className="ml-1">GOOGLE_CLIENT_SECRET</code> (and <code>GOOGLE_REDIRECT_URI</code> if the address above is
          not where the app is served), then restart it.
        </li>
      </ol>
      <p className="mt-3 text-xs text-ink-500">The README has the same steps, with more detail.</p>
    </Panel>
  );
}

interface ReviewProps {
  alert: EmailAlertRow;
  cards: Card[];
  people: Person[];
  categories: { key: string; label: string }[];
  onDone: () => Promise<void>;
  onDismiss: () => void;
  onError: (message: string) => void;
}

function ReviewItem({ alert, cards, people, categories, onDone, onDismiss, onError }: ReviewProps) {
  const active = cards.filter((c) => c.isActive);
  const [open, setOpen] = useState(false);
  const [showText, setShowText] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({
    cardId: active.find((c) => c.last4 && c.last4 === alert.last4)?.id ?? active[0]?.id ?? "",
    merchant: alert.merchant ?? "",
    amount: alert.amount != null ? String(alert.amount) : "",
    date: alert.date ?? alert.receivedAt.slice(0, 10),
    category: categories.find((c) => c.key === "OTHER")?.key ?? categories[0]?.key ?? "",
    channel: "OFFLINE" as "ONLINE" | "OFFLINE",
    isInternational: false,
    cardholderId: "",
  }));

  async function add() {
    setSaving(true);
    try {
      await api.post(`/gmail/alerts/${alert.id}/add`, {
        ...draft,
        amount: Number(draft.amount),
        cardholderId: draft.cardholderId || null,
      });
      await onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not add it");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-ink-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-ink-900">
            {alert.merchant ?? alert.subject ?? "Card alert"}
            {alert.amount != null && <span className="ml-2 tabular-nums text-ink-700">{formatINR(alert.amount)}</span>}
          </div>
          <div className="text-xs text-ink-500">
            {alert.bank ?? "Bank"} · {alert.date ?? formatDate(alert.receivedAt)}
            {alert.last4 ? ` · card ending ${alert.last4}` : ""}
          </div>
          <p className="mt-1 text-sm text-amber-800">{alert.reason}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          {!open && (
            <button className="btn-secondary text-sm" onClick={() => setOpen(true)}>
              {alert.status === "DUPLICATE" ? "Add anyway" : "Add"}
            </button>
          )}
          <button className="btn-ghost text-sm" onClick={onDismiss}>
            {alert.status === "DUPLICATE" ? "It's the same one" : "Set aside"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 border-t border-ink-100 pt-3">
          {active.length === 0 ? (
            <p className="text-sm text-ink-600">
              <Link to="/cards" className="text-accent-700 underline">Add a card</Link> first.
            </p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="label" htmlFor={`c-${alert.id}`}>Card</label>
                  <select id={`c-${alert.id}`} className="input" value={draft.cardId} onChange={(e) => setDraft({ ...draft, cardId: e.target.value })}>
                    {active.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.last4 ? ` ···· ${c.last4}` : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor={`m-${alert.id}`}>Merchant</label>
                  <input id={`m-${alert.id}`} className="input" value={draft.merchant} onChange={(e) => setDraft({ ...draft, merchant: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor={`a-${alert.id}`}>Amount (₹)</label>
                  <input id={`a-${alert.id}`} className="input tnum" type="number" min={0} step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor={`d-${alert.id}`}>Date</label>
                  <input id={`d-${alert.id}`} className="input" type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor={`k-${alert.id}`}>Category</label>
                  <select id={`k-${alert.id}`} className="input" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor={`h-${alert.id}`}>Channel</label>
                  <select id={`h-${alert.id}`} className="input" value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value as "ONLINE" | "OFFLINE" })}>
                    <option value="OFFLINE">In store</option>
                    <option value="ONLINE">Online</option>
                  </select>
                </div>
                {people.length > 1 && (
                  <div>
                    <label className="label" htmlFor={`w-${alert.id}`}>Who spent it</label>
                    <select id={`w-${alert.id}`} className="input" value={draft.cardholderId} onChange={(e) => setDraft({ ...draft, cardholderId: e.target.value })}>
                      <option value="">Not recorded</option>
                      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                  checked={draft.isInternational}
                  onChange={(e) => setDraft({ ...draft, isInternational: e.target.checked })}
                />
                International spend
              </label>
              <div className="mt-3 flex gap-2">
                <button
                  className="btn-primary"
                  disabled={saving || !draft.cardId || !draft.merchant.trim() || !(Number(draft.amount) > 0)}
                  onClick={() => void add()}
                >
                  {saving ? "Adding…" : "Add as pending"}
                </button>
                <button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      {alert.bodyText && (
        <div className="mt-2">
          <button className="btn-ghost text-xs" onClick={() => setShowText((v) => !v)}>
            {showText ? "Hide" : "Show"} the email
          </button>
          {showText && (
            <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-ink-50 p-3 text-xs text-ink-600">
              {alert.bodyText}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
